import {
  FloatingPortal,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react";
import { AnimatePresence } from "framer-motion";

import { DisplayDate } from "src/base-components/Date";
import { FloatingWindowInternal } from "src/base-components/FloatingWindow/FloatingWindow";
import { TagPill } from "src/base-components/TagPill";
import type { CaseTemplateFormFieldsInner } from "src/clients/case-management";

type OriginalValueWindowProps = {
  field: CaseTemplateFormFieldsInner;
  originalValue: unknown;
  isOpen: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement>;
};

export const OriginalValueWindow: React.FC<OriginalValueWindowProps> = ({
  field,
  originalValue,
  isOpen,
  onClose,
  anchorRef,
}) => {
  const { refs, floatingStyles } = useFloating({
    open: isOpen,
    elements: { reference: anchorRef.current },
    placement: "right-start",
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <FloatingPortal>
          <FloatingWindowInternal
            ref={refs.setFloating}
            dataLoc={`original-value-window-${field.key}`}
            isPinned={false}
            style={floatingStyles}
            title={
              <span className="font-inter-semibold-13px text-gray-800">
                Original value
              </span>
            }
            height="h-41"
            width="w-80"
            draggable
            onClose={onClose}
            maximizable
            resizeable
          >
            <div className="min-h-0 flex-1 overflow-auto p-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-inter-normal-12px text-gray-500">
                <FormattedValue fieldType={field.type} value={originalValue} />
              </div>
            </div>
          </FloatingWindowInternal>
        </FloatingPortal>
      )}
    </AnimatePresence>
  );
};

const FormattedValue: React.FC<{
  fieldType: CaseTemplateFormFieldsInner["type"];
  value: unknown;
}> = ({ fieldType, value }) => {
  switch (fieldType) {
    case "date":
      return <DisplayDate value={value as string} />;
    case "multi_select":
      return Array.isArray(value) ? (
        <div className="flex flex-wrap gap-1">
          {value.map((item, index) => (
            <TagPill
              key={index}
              value={{ value: String(item), color: "outlined-gray" }}
            />
          ))}
        </div>
      ) : (
        <TagPill value={{ value: String(value), color: "outlined-gray" }} />
      );
    case "select":
      return (
        <TagPill value={{ value: String(value), color: "outlined-gray" }} />
      );
    default:
      return <span>{String(value)}</span>;
  }
};
